import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Handshake,
  House,
  Inbox,
  LayoutList,
  ListChecks,
  Link2,
  MessageCircle,
  Store,
  Megaphone,
  Send,
  Settings,
  Tags,
  UserCircle,
  UserRound,
  Users,
  Zap,
} from "lucide-react";
import type { ProRole } from "../../lib/types";

export type NavGroup = "primary" | "utility";
export type NavCountKey =
  | "home_needs"
  | "marketplace_requests"
  | "messages_unread";

export interface NavDestination {
  id: string;
  /** Route path. */
  to: string;
  label: string;
  icon: LucideIcon;
  group: NavGroup;
  countKey?: NavCountKey;
  /** Omitted = all pro roles. */
  roles?: ProRole[];
  /** Extra paths that should also mark this item active (e.g. detail routes). */
  matchPrefixes?: string[];
}

// Marketplace exposure is a RUNTIME PostHog flag, not a build-time env var:
// see `useMarketplaceEnabled` (reads MARKETPLACE_FLAG_KEY). The old build-time
// `MARKETPLACE_ENABLED = VITE_MARKETPLACE_ENABLED !== "false"` const was
// fail-OPEN (visible unless the env var was explicitly set, which it never was
// in any environment) and had no PostHog kill-switch — so it could not be
// toggled at runtime and shipped marketplace ON by default everywhere. The
// flag is fail-closed and flips from PostHog like every other pro flag.

// ── Marketplace pageSidebar destinations (M5 steer) ──────────────────────────
//
// Marketplace is a persistent pageSidebar mini-app (the Clients-rail idiom via
// the existing ProLayout pageSidebar seam). ONE header entry "Marketplace"
// (a PRIMARY_NAV_DESTINATIONS manifest entry); the rest
// of the navigation lives in MarketplaceSidebar passed as pageSidebar.
//
// Browse buyers + Offers are live M5-F views: Browse buyers is the
// posture-aware `/marketplace` landing for a submitted pro (see
// MarketplaceLandingGate in App.tsx); Offers is the pro's own offer ledger.
// Standing offers + Profile are the E2 views.

export interface MarketplaceSidebarDestination {
  id: string;
  /** Route path. */
  to: string;
  label: string;
  /** Rail icon (leading each item), matching the Clients/header nav idiom. */
  icon: LucideIcon;
  /** True while M5 content lands — sidebar renders this as a non-navigable
   *  item with a "Coming in M5" affordance instead of a NavLink. */
  disabled?: boolean;
  /** Extra paths that should also mark this item active. */
  matchPrefixes?: string[];
}

export interface MarketplaceSidebarGroup {
  id: string;
  label: string;
  destinations: MarketplaceSidebarDestination[];
}

/**
 * The Marketplace sidebar's three groups, in render order (plan 443 M2): what
 * needs a reply (Inbox), where to find buyers, and what the pro has
 * published. The group names are the one place small-caps labels are used.
 */
export const MARKETPLACE_SIDEBAR_GROUPS: MarketplaceSidebarGroup[] = [
  {
    id: "inbox",
    label: "Inbox",
    destinations: [
      {
        id: "marketplace-requests",
        to: "/marketplace/requests",
        label: "Requests",
        icon: Inbox,
        matchPrefixes: ["/marketplace/requests"],
      },
      {
        id: "marketplace-connections",
        to: "/marketplace/connections",
        label: "Connections",
        icon: Link2,
        matchPrefixes: ["/marketplace/connections"],
      },
    ],
  },
  {
    id: "find-buyers",
    label: "Find buyers",
    destinations: [
      {
        id: "marketplace-browse",
        to: "/marketplace/browse",
        label: "Browse buyers",
        icon: Users,
        matchPrefixes: ["/marketplace/browse"],
      },
      {
        id: "marketplace-offers-ledger",
        to: "/marketplace/offers",
        label: "Your offers",
        icon: Handshake,
        matchPrefixes: ["/marketplace/offers"],
      },
    ],
  },
  {
    id: "your-listing",
    label: "Your listing",
    destinations: [
      {
        id: "marketplace-standing-offers",
        to: "/marketplace/standing-offers",
        label: "Standing offers",
        icon: Tags,
        matchPrefixes: ["/marketplace/standing-offers"],
      },
      {
        id: "marketplace-profile",
        to: "/marketplace/profile",
        label: "Profile",
        icon: UserRound,
        matchPrefixes: ["/marketplace/profile"],
      },
    ],
  },
];

/** Ordered (grouped, flattened) sidebar destinations for the Marketplace mini-app. */
export const MARKETPLACE_SIDEBAR_DESTINATIONS: MarketplaceSidebarDestination[] =
  MARKETPLACE_SIDEBAR_GROUPS.flatMap((g) => g.destinations);

// ── Engagement group (two-level nav, deliberate trip-wire crossing) ─────────
//
// The Engagement header entry is a GROUP, not a direct link. It owns a sub-nav
// row (rendered by SubNav.tsx) containing the product tabs: Campaigns and
// Automations. This crosses the ProLayout trip-wire intentionally — see
// frontend/pro/CLAUDE.md §Navigation shell for the documented rationale.

export interface SubNavTab {
  id: string;
  to: string;
  label: string;
  /** Leading icon in the below-`xl` mobile drawer, where every row shares one
   *  icon/label column. The desktop SubNav row stays text-only. */
  icon?: LucideIcon;
  matchPrefixes?: string[];
}

export interface EngagementGroup {
  id: "engagement";
  label: string;
  icon: LucideIcon;
  /** All matchPrefixes across every sub-nav tab — used to mark the header
   *  entry active when any sub-page is visited. */
  matchPrefixes: string[];
  tabs: SubNavTab[];
}

/** The Engagement group definition (header entry + sub-nav tabs). */
export const ENGAGEMENT_GROUP: EngagementGroup = {
  id: "engagement",
  label: "Engagement",
  icon: Megaphone,
  matchPrefixes: ["/campaigns", "/automations"],
  tabs: [
    {
      id: "campaigns",
      to: "/campaigns",
      label: "Campaigns",
      // Send, not Megaphone: Megaphone is the Engagement GROUP's icon.
      icon: Send,
      matchPrefixes: ["/campaigns"],
    },
    {
      id: "automations",
      to: "/automations",
      label: "Automations",
      icon: Zap,
      matchPrefixes: ["/automations"],
    },
  ],
};

// Header-primary is intentionally flat except for the Engagement group, which
// uses a two-level shape (header group + sub-nav). Marketplace is a single flat
// header entry with pageSidebar for the rest — NOT a SubNav group.
export const PRIMARY_NAV_DESTINATIONS: NavDestination[] = [
  {
    id: "home",
    to: "/home",
    label: "Home",
    icon: House,
    group: "primary",
    countKey: "home_needs",
    matchPrefixes: ["/home"],
  },
  // Marketplace is a manifest entry now (second, right after Home). It points
  // at the `/marketplace` landing gate, not a sidebar page; HeaderNav omits it
  // unless `useMarketplaceGate() === "on"`. Its sidebar lives in
  // MarketplaceSidebar (pageSidebar).
  {
    id: "marketplace",
    to: "/marketplace",
    label: "Marketplace",
    icon: Store,
    group: "primary",
    countKey: "marketplace_requests",
    matchPrefixes: ["/marketplace"],
  },
  {
    id: "people",
    to: "/people",
    label: "People",
    icon: Users,
    group: "primary",
    matchPrefixes: ["/people"],
  },
  // Tasks (the former Today work list) carries NO count key: Home is the only
  // urgency count in the header.
  {
    id: "tasks",
    to: "/tasks",
    label: "Tasks",
    icon: ListChecks,
    group: "primary",
    matchPrefixes: ["/tasks"],
  },
  // Messages is a PRIMARY destination (operator decision 2026-07-20,
  // superseding the messaging-inbox handoff's icon-first sequencing): the
  // unified inbox is a dwelling workspace, so it earns a tab — the header
  // utility icon was removed in the same change. Primary count stays at 3
  // flat entries (+Engagement group +Marketplace) — under the 7-destination
  // trip-wire: Home · Marketplace · People · Tasks · Messages + Engagement = 6.
  // The badge counts conversations with unread messages.
  {
    id: "messages",
    to: "/messages",
    label: "Messages",
    icon: MessageCircle,
    group: "primary",
    countKey: "messages_unread",
    matchPrefixes: ["/messages"],
  },
];

export const UTILITY_NAV_DESTINATIONS: NavDestination[] = [
  {
    id: "activity",
    to: "/activity",
    label: "Activity",
    icon: Activity,
    group: "utility",
    matchPrefixes: ["/activity"],
  },
  {
    id: "settings",
    to: "/settings",
    label: "Settings",
    icon: Settings,
    group: "utility",
    matchPrefixes: ["/settings"],
  },
  {
    id: "profile",
    to: "/profile",
    label: "Profile",
    icon: UserCircle,
    group: "utility",
    matchPrefixes: ["/profile"],
  },
];

export const NAV_DESTINATIONS: NavDestination[] = [
  ...PRIMARY_NAV_DESTINATIONS,
  ...UTILITY_NAV_DESTINATIONS,
];

// Templates remains reachable as a library destination under the CRM nav
// (via TemplatesPage) but is no longer a top-level primary nav tab.
export const TEMPLATES_LIBRARY: NavDestination = {
  id: "templates",
  to: "/templates",
  label: "Templates",
  icon: LayoutList,
  group: "utility",
  matchPrefixes: ["/templates"],
};

// Library group — shown as a labeled nav group below the primary destinations.
// Contains findable CRM resources that aren't products (today: Templates only).
// C1: surfaced in HeaderNav as "Library" with the Templates entry.
export const LIBRARY_NAV_DESTINATIONS: NavDestination[] = [TEMPLATES_LIBRARY];

// Zap re-exported so callers don't need a direct lucide import for the
// Automations icon (the same icon the Automations tab carries above).
export { Zap as AutomationsIcon };
